import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
    ProvisionSimulationBookingDto,
    ProvisionSimulationEntitlementDto
} from '../../../model/dto/api/provision-simulation.dto';
import {provisionSimulationEnumLabel} from '../../../enum/provision-simulation-technical-label';
import {ProvisionAgentRoleEnum} from '../../../enum/provision-agent-role.enum';
import {ProvisionLiabilitySource} from '../../../enum/provision-liability-source.enum';
import {ProvisionLiabilityStatus} from '../../../enum/provision-liability-status.enum';
import {ProvisionTransactionEvent} from '../../../enum/provision-transaction-event.enum';
import {ProvisionAccountKind} from '../../../enum/provision-account-kind.enum';

/**
 * The "Ergebnis" of a simulation: one row per Provisionssatz the run would create, with the fields
 * a real `cp.ProvisionContractAgent` carries — component, Abrechnungskonto, matrix and matrix row,
 * factor, liability type and where it came from, amounts, reserve split, liability period, and the
 * bookings a run would make.
 *
 * ── THE ANSWER, NOT THE EXPLANATION
 *
 * This component shows WHAT came out; {@link CpProvisionSimulationProtocolComponent} shows HOW.
 * The two are separate because a reader who only wants the amount should not have to walk 37 steps,
 * and a reader who disputes the amount must be able to walk them.
 *
 * ── NOTHING IS CALCULATED AND NOTHING IS SUMMED
 *
 * Every amount arrives finished from the productive engine. In particular there is deliberately NO
 * total row: a sum of the delivered rows would be a second number beside the one the commission run
 * pays out, computed in a second place, and it is exactly the kind of number that later gets quoted
 * back as "the simulation said X". If a total is wanted it belongs in the endpoint, next to the
 * figures it must agree with.
 *
 * ── CAPTIONS COME FROM THE SERVER; THE MAP IS ONLY THE FALLBACK
 *
 * The structured enum fields of the Satzkopf (`agentRole`, `calculationMode`, `liabilitySource`,
 * `liabilityStatus`) and of a booking (`event`, `accountKind`) arrive as the ORDINAL beside a
 * sibling `…Info` block that carries the German label from the backend's `@UiEnumValueInfo`. The
 * label is read from there. Only a response WITHOUT the block — a backend deployed before the
 * frontend, which happens because the backend ships by hand and the frontend by CI — falls back:
 * a string value goes through the technical-label map, a bare ordinal through the FE enum mirror
 * and then the same map. Every branch that STYLES by value goes through the FE enum, never the raw
 * string. The rule lives ONCE, in {@link provisionSimulationEnumLabel}; this component only calls it.
 *
 * ── FACTOR AND ORIGIN REUSE THE MODULE'S OWN OUTPUT FIELDS
 *
 * The factor is rendered by {@link CpFormFieldOutputFactorComponent}, which already carries the
 * rule that factor 0 is a STATEMENT and never an empty cell, and which prints the Berechnungsart
 * beneath the number because 12 means twelve monthly premiums under MONTHLY_PREMIUM and twelve
 * thousandths under PER_MILLE — two values three orders of magnitude apart. Reusing it here is what
 * keeps the simulation speaking the same language as the Beratersicht.
 *
 * ── THE NEGATIVE ID IS SHOWN ON PURPOSE
 *
 * `entitlementRef` is negative because nothing in this answer exists in the database. It is the
 * join key of the protocol steps and is printed so a reader can follow a step back to its row.
 */
@Component({
    selector: 'cp-provision-simulation-entitlements',
    templateUrl: './cp-provision-simulation-entitlements.component.html',
    styleUrls: ['./cp-provision-simulation-entitlements.component.scss'],
    standalone: false
})
export class CpProvisionSimulationEntitlementsComponent implements OnInit, OnChanges {

    initialized: boolean;

    /** The Provisionssätze the simulation produced, in the delivered order. */
    @Input() entitlements: ProvisionSimulationEntitlementDto[] = [];

    /** The rows whose detail (liability period + bookings) is currently expanded. */
    expandedRows: { [key: string]: boolean } = {};

    ngOnInit(): void {
        this.initComponent();
    }

    /** Resets the expansion state and opens the component for change detection. */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        this.refreshComponent();
    }

    /** A new answer closes every expansion — row -1 of the last run is not row -1 of this one. */
    refreshComponent(): void {
        this.expandedRows = {};
    }

    /** German caption of the paid role (`main` / `support` / `inquired` / `calculated`). */
    roleLabel(entitlement: ProvisionSimulationEntitlementDto): string {
        return provisionSimulationEnumLabel(entitlement?.agentRoleInfo, entitlement?.agentRole, ProvisionAgentRoleEnum);
    }

    /** German caption of where the liability type came from (assignment or one of the overwrites). */
    liabilitySourceLabel(entitlement: ProvisionSimulationEntitlementDto): string {
        return provisionSimulationEnumLabel(entitlement?.liabilitySourceInfo,
            entitlement?.liabilitySource, ProvisionLiabilitySource);
    }

    /** German caption of the liability status (`ACTIVE` / `EXPIRED` / `CANCELLED` / `SUSPENDED`). */
    liabilityStatusLabel(entitlement: ProvisionSimulationEntitlementDto): string {
        return provisionSimulationEnumLabel(entitlement?.liabilityStatusInfo,
            entitlement?.liabilityStatus, ProvisionLiabilityStatus);
    }

    /** German caption of a booking's engine event. */
    bookingEventLabel(booking: ProvisionSimulationBookingDto): string {
        return provisionSimulationEnumLabel(booking?.eventInfo, booking?.event, ProvisionTransactionEvent);
    }

    /** German caption of the account a booking hits. */
    accountKindLabel(booking: ProvisionSimulationBookingDto): string {
        return provisionSimulationEnumLabel(booking?.accountKindInfo, booking?.accountKind, ProvisionAccountKind);
    }

    /**
     * True when this row would book nothing.
     *
     * Measured on the reference tenant, three of four components of a normal contract resolve to
     * the catch-all row with factor 0 and produce no booking at all. That is a real answer — the
     * configuration says "for this combination there is expressly no commission" — so the row stays
     * visible and merely says so. Hiding it would turn a deliberate zero into a missing row.
     */
    isWithoutBooking(entitlement: ProvisionSimulationEntitlementDto): boolean {
        return !entitlement?.bookings?.length;
    }

}
